import { useQueryClient } from '@tanstack/react-query';
import { authQueries } from 'src/api/queries';
import { userGuidingApi } from 'src/lib/services/api/user-guiding-api';
import * as ug from './user-guiding';
import { UserGuiding } from './user-guiding';
import { profileClient, ProfileResponse } from 'src/lib/services/api/profile-api';

type UseUserGuidingInterface = {
  identify: () => Promise<void>;
  update: () => Promise<void>;
  load: () => Promise<UserGuiding>;
  loadAndIdentify: () => Promise<void>;
  pageCapture: () => void;
  shutdown: () => void;
};

export const useUserGuiding = (): UseUserGuidingInterface => {
  const queryClient = useQueryClient();

  const identify = async () => {
    // Get cached profile data or fetch it
    const profile = queryClient.getQueryData<ProfileResponse>([authQueries.profile])
      ?? await queryClient.fetchQuery({
        queryKey: [authQueries.profile],
        queryFn: () => profileClient.get(),
      });

    if (!profile?.id) {
      return;
    }

    const payload = await queryClient.fetchQuery({
      queryKey: ['user-guiding-payload', profile.id],
      queryFn: ({ signal }) => userGuidingApi.payload(profile.id, { signal }),
    });

    const data: Parameters<NonNullable<UserGuiding>['identify']> = [
      profile.id,
      {
        ...payload.attributes,
        is_admin: true, // Mark as admin user for UserGuiding segmentation
      },
    ];

    ug.call('identify', ...data);
  };

  const update = () => identify();

  const load = async () => ug.load();

  const loadAndIdentify = async () => {
    await ug.load();
    await identify();
  };

  const pageCapture = () => ug.call('page');

  const shutdown = () => ug.shutdown();

  return {
    identify,
    update,
    load,
    loadAndIdentify,
    pageCapture,
    shutdown,
  };
};
